<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

const imageRef = ref<HTMLElement | null>(null);
const isIntersecting = ref(false);

let observer: IntersectionObserver | null = null;

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  alt: {
    type: String,
    default: "",
  },
  fit: {
    type: String,
    default: "fill",
  },
  useLazy: {
    type: Boolean,
    default: true,
  },
  tintColor: {
    type: String,
    default: "",
  },
});

const hasTintColor = computed(() => {
  return !!String(props.tintColor || "").trim();
});

const currentSrc = computed(() => {
  return isIntersecting.value || !props.useLazy ? props.src : props.placeholder;
});

const maskSize = computed(() => {
  if (props.fit === "contain" || props.fit === "cover") {
    return props.fit;
  }
  if (props.fit === "none") {
    return "auto";
  }
  if (props.fit === "scale-down") {
    return "contain";
  }
  return "100% 100%";
});

const tintStyle = computed(() => {
  const maskImage = currentSrc.value ? `url("${currentSrc.value}")` : "none";

  return {
    opacity: isIntersecting.value ? 1 : 0,
    backgroundColor: props.tintColor,
    WebkitMaskImage: maskImage,
    maskImage,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: maskSize.value,
    maskSize: maskSize.value,
  };
});

onMounted(() => {
  if (!props.useLazy) {
    isIntersecting.value = true;
    return;
  }
  observer = new IntersectionObserver(observerCallback, {
    rootMargin: "50px",
  });
  if (imageRef.value) {
    observer.observe(imageRef.value);
  }
});

function observerCallback(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      isIntersecting.value = true;
      observer?.unobserve(entry.target);
    }
  });
}

function handleLoad() {
  if (observer && imageRef.value) {
    observer.unobserve(imageRef.value);
  }
}

function handleError() {
  // imageRef.value.src = ''
}

onUnmounted(() => {
  if (observer && imageRef.value) {
    observer.unobserve(imageRef.value);
  }
});
</script>

<template>
  <div class="lazy-image">
    <span
      v-if="hasTintColor"
      ref="imageRef"
      :style="tintStyle"
      :title="props.alt || undefined"
      :aria-label="props.alt || undefined"
      :aria-hidden="props.alt ? undefined : 'true'"
      class="lazy-image__img lazy-image__img--tinted"
      role="img"
    />
    <img
      v-else
      ref="imageRef"
      :style="{
        opacity: isIntersecting ? 1 : 0,
        objectFit: props.fit,
      }"
      :src="currentSrc"
      :alt="props.alt"
      :loading="props.useLazy ? 'lazy' : 'eager'"
      referrerpolicy="no-referrer"
      class="lazy-image__img"
      @load="handleLoad"
      @error="handleError"
    />
    <div v-if="props.useLazy && !isIntersecting" class="lazy-image__placeholder">
      <slot name="placeholder">
        <div class="placeholder-content"></div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.lazy-image {
  position: relative;
}
.lazy-image__img {
  display: block;
  width: 100%;
  height: 100%;
}
.lazy-image__img--tinted {
  background-position: center;
}
.lazy-image__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.placeholder-content {
  color: #999;
  font-size: 14px;
}
</style>
